import { useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import EditableTitle from '@/design_system/Typography/EditableTitle';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useProduct, useUpdateProduct } from '@/models/product';
import { ErrorState } from '@/routes/SettingsBeta/components/ErrorState';
import { SettingsBreadcrumbParent } from '@/routes/SettingsBeta/components/Layout/Layout';
import { Loading } from '@/routes/SettingsBeta/components/Loading';

import { BrandSection } from './BrandSection';
import { ColorsWithImageSection } from './ColorsWithImageSection';
import { ExternalIdSection } from './ExternalIdSection';
import { SizesSection } from './SizesSection';

export const Product = () => {
  const { t } = useLingui();
  const { id } = useParams();
  const { data: product, isLoading: isProductLoading, error } = useProduct(id);
  const { mutateAsync: updateProduct, isPending: isUpdateProductPending } = useUpdateProduct(id);

  if (isProductLoading) {
    return <Loading />;
  }

  if (!product || error) {
    return (
      <ErrorState
        errorMessage={
          <Paragraph>
            <Trans id="settings.organization.data-catalogs.products.detail.errors.not-found">
              This product could not be found or you do not have access to it.
            </Trans>
          </Paragraph>
        }
      />
    );
  }

  return (
    <main>
      <Stack gap="1rem">
        <Stack
          row
          className="settings-section__record-header"
          gap="0.5rem"
          alignItems="center"
          flexWrap="wrap"
        >
          <SettingsBreadcrumbParent
            to="/settings_beta/catalog/products"
            label={<Trans id="settings.organization.data-catalogs.products">Products</Trans>}
          />
          <EditableTitle
            value={product.name}
            onSave={(newName) => updateProduct({ name: newName })}
            titleProps={{ as: 'h1', size: '200', weight: 'medium' }}
            editAriaLabel={t({
              id: 'settings.organization.data-catalogs.products.detail.editable-title.edit',
              message: 'Edit product name',
            })}
            saveAriaLabel={t({
              id: 'settings.organization.data-catalogs.products.detail.editable-title.save',
              message: 'Save product name',
            })}
            cancelAriaLabel={t({
              id: 'settings.organization.data-catalogs.products.detail.editable-title.cancel',
              message: 'Cancel renaming',
            })}
            inputAriaLabel={t({
              id: 'settings.organization.data-catalogs.products.detail.editable-title.input',
              message: 'Product name',
            })}
            isSaving={isUpdateProductPending}
          />
        </Stack>

        <ExternalIdSection product={product} />

        <BrandSection product={product} />

        <SizesSection product={product} />

        <ColorsWithImageSection product={product} />
      </Stack>
    </main>
  );
};
